import React from "react";
import { XMarkIcon, ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "@tanstack/react-router";
import prettyBytes from "pretty-bytes";
import { downloadFile } from "../../../utils/helpers";
import { Button } from '../../ui';

function FileProgressRow({ file, status, progress, error }) {
  const getStatusInfo = () => {
    switch (status) {
      case "pending":
        return {
          text: "Queued",
          color: "text-muted-foreground",
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      case "uploading":
        return {
          text: `Uploading (${Math.round(progress)}%)`,
          color: "text-info",
          icon: (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )
        };
      case "compressing":
        return {
          text: "Processing...",
          color: "text-accent",
          icon: (
            <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          )
        };
      case "success":
        return {
          text: "Complete",
          color: "text-success",
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )
        };
      case "error":
        return {
          text: "Error",
          color: "text-danger",
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )
        };
      default:
        return {
          text: "Waiting...",
          color: "text-muted",
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
    }
  };

  const { text, color, icon } = getStatusInfo();

  return (
    <div className="py-3 px-4 border-b border-border/50 last:border-b-0 hover:bg-background-soft/30 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`${color} flex-shrink-0`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate" title={file.name}>
              {file.name}
            </span>
            <span className={`${color} font-semibold text-xs whitespace-nowrap`}>{text}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground">{prettyBytes(file.size)}</span>
            {status === "uploading" && (
              <span className="text-xs text-info font-medium">{Math.round(progress)}%</span>
            )}
          </div>
        </div>
      </div>
      {status === "uploading" && (
        <div className="mt-2 w-full bg-background-soft rounded-full h-1.5 overflow-hidden">
          <div
            className="bg-accent h-1.5 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}
      {status === "error" && error && (
        <div className={`mt-2 px-3 py-2 border rounded-lg text-xs break-words whitespace-pre-line ${error.startsWith("❌")
            ? "bg-warning-soft/50 border-warning-border/60 text-warning-soft-foreground"
            : "bg-danger-soft/50 border-danger-border/60 text-danger-soft-foreground"
          }`}>
          {error}
        </div>
      )}
    </div>
  );
}

export default function BatchUploadProgressModal({ batchState, onClose, onRetryBatch }) {
  const navigate = useNavigate();

  if (!batchState || batchState.status === 'idle') {
    return null;
  }

  const { files, overallProgress, status, currentFileIndex } = batchState;
  const totalFiles = files.length;
  const completedFiles = files.filter(f => f.status === 'success').length;
  const currentFileNumber = Math.min(currentFileIndex + 1, totalFiles);

  const getOverallStatusText = () => {
    switch (status) {
      case "uploading":
        return `Uploading file ${currentFileNumber} of ${totalFiles}...`;
      case "error":
        const errorFile = files.find(f => f.status === 'error');
        return `Error uploading ${errorFile ? `"${errorFile.file.name}"` : 'file'}. Batch stopped.`;
      case "complete":
        return `Batch complete. ${completedFiles} of ${totalFiles} files uploaded successfully.`;
      default:
        return "Preparing upload...";
    }
  };

  const isBatchError = status === 'error';
  const isBatchComplete = status === 'complete';
  const isBatchInProgress = status === 'uploading';
  const canRetry = isBatchError; // Simple retry: retry the whole batch from start
  const allowClose = isBatchComplete || isBatchError; // Only allow closing when finished or failed

  const handleDownloadAll = async () => {
    const successfulFiles = files.filter(f => f.status === 'success');
    for (const fileState of successfulFiles) {
      if (fileState.compressed_file_url) {
        downloadFile(fileState.compressed_file_url);
        // Add a small delay to prevent browser from blocking multiple downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background-elevated rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col border border-border/60">
        <div className="flex justify-between items-center px-6 py-5 border-b border-border/50">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-foreground">Upload progress</h2>
          </div>
          <button
            onClick={onClose}
            className={`text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-background-soft ${isBatchInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label="Close"
            disabled={isBatchInProgress}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {isBatchInProgress && (
          <div className="mx-6 mt-4 p-3.5 bg-warning-soft/50 border border-warning-border/60 rounded-lg flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-warning-soft-foreground leading-relaxed">
              Keep this page open until upload completes. Closing this tab will stop the process.
              <span className="block mt-1 opacity-80">(Switching to other tabs is okay)</span>
            </p>
          </div>
        )}

        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">{getOverallStatusText()}</p>
            {status === 'uploading' && (
              <span className="text-xs font-semibold text-accent tabular-nums">
                {Math.round(overallProgress)}%
              </span>
            )}
          </div>
          {status === 'uploading' && totalFiles > 0 && (
            <div className="w-full bg-background-soft rounded-full h-2 overflow-hidden">
              <div
                className="bg-accent h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
          )}
          {isBatchComplete && (
            <div className="flex items-center gap-2 px-3 py-2 bg-success-soft/50 border border-success-border/60 rounded-lg">
              <svg className="w-4 h-4 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-success-soft-foreground">
                All uploads completed successfully
              </span>
            </div>
          )}

        </div>

        <div className="overflow-y-auto flex-1 border-t border-border/50 bg-background-elevated">
          {files.map((fileState, index) => (
            <FileProgressRow
              key={index} // Using index is okay here as the list order is fixed during upload
              file={fileState.file}
              status={fileState.status}
              progress={fileState.progress}
              error={fileState.error}
            />
          ))}
        </div>

        {/* AI Studio Promotion Banner */}
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-t border-border/50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <SparklesIcon className="w-4 h-4 text-accent" />
                <h3 className="text-sm font-semibold text-foreground">Generate 3D models with AI</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Create high-quality 3D assets from text or images in seconds with our new AI Studio.
              </p>
            </div>
            <Button
              variant="accent"
              size="sm"
              onClick={() => navigate({ to: '/app/ai-studio' })}
              disabled={isBatchInProgress}
              className="whitespace-nowrap shadow-sm"
              title={isBatchInProgress ? "Wait for upload to complete" : "Go to AI Studio"}
            >
              Try AI Studio
            </Button>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border/50 flex justify-between items-center gap-3 bg-background-soft/30">
          <div className="text-xs text-muted-foreground">
            {completedFiles} of {totalFiles} completed
          </div>
          <div className="flex gap-2">
            {canRetry && onRetryBatch && (
              <Button
                variant="danger"
                size="sm"
                onClick={onRetryBatch}
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry
              </Button>
            )}
            {isBatchComplete && files.some(f => f.status === 'success') && (
              <Button
                onClick={handleDownloadAll}
                size="sm"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {files.filter(f => f.status === 'success').length === 1 ? 'Download' : 'Download all'}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={!allowClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 